import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, fetchUser, user } = useAuth();

  // Email bisa datang dari: (1) location.state (Login.js navigate), atau
  // (2) user dari AuthContext (ketika App.js guard redirect tanpa state)
  const stateEmail = location.state?.email || '';
  const mustChangePw = location.state?.mustChangePassword ?? (user?.must_change_password ?? true);

  const [email, setEmail] = useState(stateEmail || user?.email || '');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Sync email dari user context jika email masih kosong (user dimuat async)
  useEffect(() => {
    if (!email && user?.email) {
      setEmail(user.email);
    }
  }, [user, email]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const code = digits.join('');
  const resolvedEmail = email.trim();
  // Email di-lock (tidak bisa diedit) jika sudah diketahui dari state atau user context
  const emailLocked = !!(stateEmail || user?.email);

  const handleDigitChange = (idx, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    setError('');
    if (v && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) inputRefs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || '';
    setDigits(next);
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleVerify = async () => {
    if (code.length !== 6) { setError('Masukkan 6 digit kode verifikasi.'); return; }
    if (!resolvedEmail) { setError('Masukkan email kamu terlebih dahulu.'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/verify-email`, { email: resolvedEmail.toLowerCase(), code });
      setSuccess(true);
      if (token && typeof fetchUser === 'function') await fetchUser(token);
      setTimeout(() => {
        if (mustChangePw) navigate('/first-login');
        else navigate('/dashboard');
      }, 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Kode verifikasi salah atau sudah kedaluwarsa.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!resolvedEmail) { setError('Masukkan email kamu terlebih dahulu.'); return; }
    setResending(true);
    setError('');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: resolvedEmail.toLowerCase() });
      setResendCooldown(120);
    } catch (err) {
      const msg = err?.response?.data?.detail || '';
      if (msg.includes('2 menit')) setResendCooldown(120);
      setError(msg || 'Gagal mengirim ulang. Coba lagi.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: '#1A5733',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: '800', fontSize: '18px',
            }}>S</div>
            <span style={{ fontWeight: '700', fontSize: '20px', color: '#111' }}>Stockara</span>
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
          padding: '40px 36px',
          border: '1px solid #eef0f2',
        }}>

          {success ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#f0fdf4', border: '2px solid #86efac',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '700', color: '#111' }}>
                Email terverifikasi!
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                Mengarahkan ke langkah berikutnya...
              </p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '28px' }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '12px',
                  background: '#f0f7f3',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '16px',
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="4" width="20" height="16" rx="3" stroke="#1A5733" strokeWidth="1.8"/>
                    <path d="M2 8l10 6 10-6" stroke="#1A5733" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <h1 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '700', color: '#111' }}>
                  Verifikasi Email
                </h1>
                <p style={{ margin: 0, fontSize: '14px', color: '#555', lineHeight: 1.6 }}>
                  Kode 6 digit telah dikirimkan ke email kamu.
                  Masukkan kode tersebut untuk mengaktifkan akun.
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block', fontSize: '13px', fontWeight: '600',
                  color: '#374151', marginBottom: '6px',
                }}>
                  Email
                </label>
                {emailLocked ? (
                  <div style={{
                    padding: '11px 14px', borderRadius: '10px',
                    border: '1.5px solid #e2e8f0', fontSize: '14px',
                    background: '#f9fafb', color: '#374151',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
                  }}>
                    <span style={{ fontWeight: '500' }}>{resolvedEmail}</span>
                    <button
                      onClick={() => navigate('/login')}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#6b7280', fontSize: '12px', padding: 0,
                        flexShrink: 0, textDecoration: 'underline',
                      }}
                    >
                      Bukan kamu?
                    </button>
                  </div>
                ) : (
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="email@kamu.com"
                    autoFocus
                    style={{
                      width: '100%', padding: '11px 14px', borderRadius: '10px',
                      border: '1.5px solid #e2e8f0', fontSize: '14px',
                      outline: 'none', boxSizing: 'border-box', color: '#111',
                    }}
                    onFocus={e => (e.target.style.borderColor = '#1A5733')}
                    onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
                  />
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block', fontSize: '13px', fontWeight: '600',
                  color: '#374151', marginBottom: '12px',
                }}>
                  Kode Verifikasi
                </label>
                <div
                  style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
                  onPaste={handlePaste}
                >
                  {digits.map((d, i) => (
                    <input
                      key={i}
                      ref={el => (inputRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={e => handleDigitChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      style={{
                        width: '48px', height: '56px',
                        textAlign: 'center',
                        fontSize: '22px', fontWeight: '700',
                        borderRadius: '12px',
                        border: `2px solid ${d ? '#1A5733' : '#e2e8f0'}`,
                        background: d ? '#f0f7f3' : 'white',
                        color: '#111',
                        outline: 'none',
                        transition: 'border-color 0.15s, background 0.15s',
                        fontFamily: 'monospace',
                      }}
                      onFocus={e => { if (!e.target.value) e.target.style.borderColor = '#6bb089'; }}
                      onBlur={e => { if (!e.target.value) e.target.style.borderColor = '#e2e8f0'; }}
                    />
                  ))}
                </div>
              </div>

              {error && (
                <div style={{
                  padding: '10px 14px', borderRadius: '10px',
                  background: '#fef2f2', border: '1px solid #fecaca',
                  fontSize: '13px', color: '#dc2626', marginBottom: '16px',
                }}>
                  {error}
                </div>
              )}

              <button
                onClick={handleVerify}
                disabled={loading || code.length !== 6 || !resolvedEmail}
                style={{
                  width: '100%', padding: '14px', borderRadius: '12px',
                  background: (loading || code.length !== 6 || !resolvedEmail) ? '#e2e8f0' : '#1A5733',
                  color: (loading || code.length !== 6 || !resolvedEmail) ? '#9ca3af' : 'white',
                  fontWeight: '600', fontSize: '15px', border: 'none',
                  cursor: (loading || code.length !== 6 || !resolvedEmail) ? 'not-allowed' : 'pointer',
                  marginBottom: '16px',
                  transition: 'background 0.2s',
                }}
              >
                {loading ? 'Memverifikasi...' : 'Verifikasi Akun'}
              </button>

              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#888' }}>
                  Tidak menerima kode?
                </p>
                {resendCooldown > 0 ? (
                  <p style={{ margin: 0, fontSize: '13px', color: '#aaa' }}>
                    Kirim ulang dalam {resendCooldown}s
                  </p>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={resending || !resolvedEmail}
                    style={{
                      background: 'none', border: 'none',
                      cursor: (resending || !resolvedEmail) ? 'not-allowed' : 'pointer',
                      color: (resending || !resolvedEmail) ? '#aaa' : '#1A5733',
                      fontSize: '13px', fontWeight: '600',
                      padding: 0, textDecoration: 'underline',
                    }}
                  >
                    {resending ? 'Mengirim...' : 'Kirim ulang kode'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#aaa', marginTop: '20px' }}>
          Kode berlaku 48 jam. Butuh bantuan?{' '}
          <a href="mailto:support@stockara.my.id" style={{ color: '#1A5733' }}>Hubungi kami</a>
        </p>
      </div>
    </div>
  );
}